/**
 * Helpers de planes. Oferta vigente: starter, professional, enterprise
 * (free y business retirados de la oferta, ver RETIRED_PLAN_IDS).
 */

/**
 * Planes sales-touch cuyo precio NO se muestra: el setup es custom
 * (multi-restaurante, whitelabel, SLA, Account Manager) y se cierra con ventas,
 * no por self-service checkout.
 *
 * Se resuelve por `planId` porque el backend NO envía un flag de precio custom
 * para enterprise (solo `monthly: 199000`), así que confiar solo en
 * `pricing.custom` dejaba el precio visible y permitía un checkout de MP para
 * enterprise. NER-3236 (decisión Diego): mostrar "Consultanos" en vez del número.
 */
export const CUSTOM_PRICING_PLAN_IDS = ['enterprise'];

/**
 * ¿El plan oculta su precio (muestra "Consultanos") y es sales-touch?
 * Cubre los planes de CUSTOM_PRICING_PLAN_IDS además de cualquier precio
 * marcado con `custom: true` (flag explícito, por si el backend lo agrega).
 *
 * @param {object} plan  Plan con `planId` y `pricing`.
 * @param {string} [currency]  Moneda seleccionada (ARS/USD/BRL). Default: ARS.
 * @returns {boolean}
 */
export function hasCustomPricing(plan, currency) {
  if (!plan) return false;
  if (CUSTOM_PRICING_PLAN_IDS.includes(plan.planId)) return true;
  const pricing = (currency && plan.pricing?.[currency]) || plan.pricing?.ARS || {};
  return !!(pricing.custom || plan.pricing?.custom);
}

/**
 * Oferta vigente (Fase 1): Starter → Profesional → Enterprise.
 * FREE y Business se retiraron de la oferta: el backend los deja con
 * `active: false` (y `listPlans` ya los filtra), pero los excluimos también
 * por id como red de seguridad por si el orden de deploy no acompaña.
 * FREE sigue existiendo internamente (clientes legacy + baja de fin de trial),
 * así que NO se usa esta lista para gating ni para mostrar el plan actual.
 */
export const RETIRED_PLAN_IDS = ['free', 'business'];

/** Días de prueba por defecto de los planes self-service con trial. */
export const DEFAULT_TRIAL_DAYS = 30;

/**
 * Planes con prueba gratis cuando el backend todavía no expone `trialDays`
 * (fallback para no depender del orden de deploy backend/admin).
 */
export const TRIAL_PLAN_IDS = ['starter', 'professional'];

/**
 * ¿El plan se puede elegir (se muestra en las listas de planes)?
 * @param {object} plan Plan con `planId` y opcional `active`.
 * @returns {boolean}
 */
export function isPlanOffered(plan) {
  if (!plan || !plan.planId) return false;
  if (plan.active === false) return false;
  return !RETIRED_PLAN_IDS.includes(plan.planId);
}

/**
 * Filtra y ordena (por `order`) los planes elegibles.
 * @param {Array<object>} plans
 * @returns {Array<object>} copia nueva, sin mutar el array original
 */
export function getOfferedPlans(plans) {
  if (!Array.isArray(plans)) return [];
  return plans.filter(isPlanOffered).sort((a, b) => (a.order || 0) - (b.order || 0));
}

/**
 * Días de prueba gratis del plan. Usa `plan.trialDays` si el backend lo manda;
 * si no, cae al fallback por planId (TRIAL_PLAN_IDS → 30). Los planes
 * sales-touch (Enterprise) nunca tienen trial self-service.
 *
 * @param {object|string} plan Plan (o planId).
 * @returns {number} días de prueba (0 = sin prueba)
 */
export function getTrialDays(plan) {
  if (!plan) return 0;
  const planId = typeof plan === 'string' ? plan : plan.planId;
  if (!planId || CUSTOM_PRICING_PLAN_IDS.includes(planId)) return 0;
  if (typeof plan === 'object' && plan.trialDays !== undefined && plan.trialDays !== null) {
    const days = Number(plan.trialDays);
    return Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
  }
  return TRIAL_PLAN_IDS.includes(planId) ? DEFAULT_TRIAL_DAYS : 0;
}

/**
 * ¿El alta en este plan arranca con prueba gratis?
 * @param {object|string} plan Plan (o planId).
 * @returns {boolean}
 */
export function hasTrial(plan) {
  return getTrialDays(plan) > 0;
}

/**
 * Texto de una feature de plan para las cards: tal cual viene del backend
 * (siglas como QR, KDS, AFIP, NC, API intactas); solo asegura la primera letra
 * en mayúscula.
 */
export function formatFeatureText(text) {
  const value = String(text ?? '');
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}
